/**
 * buildInfo — the app-facing view of the deployed build/version ref.
 *
 * WHY this exists (FR-FE-BUG-TOOL-EXPAND-DIAGNOSTICS-AND-VERSION-REF-2026-06-01):
 * Cloud Run builds here are non-hermetic — the SAME git commit has produced
 * multiple DISTINCT images, and Cloud Run's `commit-sha` label is identical
 * across them, so it cannot attribute a user bug to a deployed revision.
 *
 * The three compile-time defines (__BUILD_HASH__ / __APP_BUILD__ /
 * __BUILD_TIME__) are wired in apps/command/vite.config.ts. Reading them is NOT
 * done here: this file used to re-implement the same
 * `typeof X !== 'undefined' ? X : 'dev'` rule that scoutLog and the query
 * persister each carried their own copy of — three copies of one rule, all three
 * inheriting the same defect (the prod image has no git, so 'dev' shipped to
 * production while the DEV SERVER reported a real SHA — the labels were
 * inverted). The rule now has exactly one home: @appic/lib's buildIdentity.
 */

import { getBuildIdentity, BUILD_IDENTITY_UNRESOLVED } from '@appic/lib/utils/buildIdentity'

export interface BuildInfo {
  /** Git short SHA the bundle was built from, or 'unresolved'. */
  commit: string
  /** Cloud Build identifier ($SHORT_SHA-$BUILD_ID) — distinguishes rebuilds (or 'unresolved'). */
  build: string
  /** ISO timestamp the bundle was built (or 'unresolved'). */
  builtAt: string
}

export function getBuildInfo(): BuildInfo {
  const { commit, build, builtAt } = getBuildIdentity()
  return { commit, build, builtAt }
}

/**
 * Compact, human-readable version ref for an always-visible footer/about line.
 * Prefers the Cloud Build id (unique per rebuild); appends the commit when it
 * adds signal. Example: `a1b2c3d-4567 (a1b2c3d)` · `a1b2c3d` · `unresolved`.
 */
export function formatBuildRef(info: BuildInfo = getBuildInfo()): string {
  if (info.build !== BUILD_IDENTITY_UNRESOLVED) {
    return info.commit !== BUILD_IDENTITY_UNRESOLVED && !info.build.startsWith(info.commit)
      ? `${info.build} (${info.commit})`
      : info.build
  }
  return info.commit
}
